import React, { useState } from 'react';
import { MenuItem } from '@mui/material';
import { useDropzone } from 'react-dropzone';
import MBImage from 'components/components/MBImage';
import palette from 'theme/palette';
import * as Styled from './UploadImageModal.styles';
import { UploadImageModalProps } from './UploadImageModal.types';
import { Status } from '../../../typings/enums';

const DEFAULT_ALLOWED_FILES = '.png, .jpg, .jpeg, .heic, .heif, .webp';

type FileItemProps = {
  url: string;
  status: Status;
  filename?: string;
};

const wrongFormatCode = 'file-invalid-type';
const tooManyFilesCode = 'too-many-files';

const FileItem: React.FC<FileItemProps> = ({ url, status }) => {
  return (
    <Styled.FileItem>
      <Styled.FileImageWrapper status={status}>
        <MBImage aspectRatio="original" imageUrl={url} borderRadius={12} />
      </Styled.FileImageWrapper>
    </Styled.FileItem>
  );
};

const UploadImageModal: React.FC<UploadImageModalProps> = (props) => {
  const {
    onFilesSelected,
    open,
    onClose,
    onAdd,
    isLoading,
    maxFilesCount = 1,
    backgroundModes = [],
    onBackgroundModeClick,
    onSearchFormSubmit,
    selectedBackgroundMode,
    title = 'Add an Image',
    subtitle = 'Images you add will appear on your palette and can be used as background images.',
    submitButtonLabel = 'Add',
    cancelButtonLabel = 'Cancel',
    uploadButtonLabel = 'Upload a file',
    dragTextLabel = 'Drag an image here',
    orTextLabel = 'or',
    searchInputButton = 'Search',
    searchInputPlaceholder = 'Paste image link',
    uploadingTextLabel = 'Uploading',
    wrongFormatErrorLabel = 'File type must be one of',
    tooManyFilesErrorLabel = 'Too many files',
    isPalette = false,
    showCloseIcon,
    showLinkForm = false,
    showBottomActions = true,
    showPreviews = true,
    customErrorMessages = [],
  } = props;
  const [linkValue, setLinkValue] = useState('');
  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
    fileRejections,
  } = useDropzone({
    ...props,
    useFsAccessApi: false,
    multiple: Number(maxFilesCount) > 1,
    accept: ['.png', '.jpeg', '.jpg', '.heic', '.heif', '.webp'],
    onDropAccepted: (newFiles) => {
      const neededFiles = [];
      const length = maxFilesCount || newFiles.length;
      for (let i = 0; i < length; i++) {
        const file = newFiles[i];
        if (file != null) {
          const newFile = Object.assign(file, {
            preview: URL.createObjectURL(file),
            status: Status.IDLE,
          });
          neededFiles.push(newFile);
        }
      }
      if (neededFiles.length > 0) {
        onFilesSelected(neededFiles);
      }
    },
  });

  const handleLinkValueChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setLinkValue(event.target.value);
  };

  const showBackgroundPicker =
    backgroundModes?.length && onBackgroundModeClick != null;
  const textPostfix = !isDragAccept ? orTextLabel : '';
  const mainColor = palette.primary.main;
  const secondaryColor = palette.grey.A400;
  const errorItem = fileRejections?.[0];

  const customErrors = customErrorMessages.map((item) => (
    <Styled.Paragraph
      pt="0.5em"
      pb="0"
      key={item}
      variant="micro"
      color={palette.error.main}
    >
      {item}
    </Styled.Paragraph>
  ));

  const handleSearchFormClick = () => {
    onSearchFormSubmit?.(linkValue);
  };

  const handleClose = () => {
    setLinkValue('');
    onClose?.();
  };

  return (
    <Styled.Dialog
      open={open}
      onClose={handleClose}
      title={title}
      applyButtonLabel={submitButtonLabel}
      cancelButtonLabel={cancelButtonLabel}
      isPalette={isPalette}
      dialogPaletteUnitsSize={1.95}
      onApplyButtonClick={onAdd}
      onCancelButtonClick={handleClose}
      isApplyLoading={!props.files?.length || isLoading}
      dialogWidth={480}
      titleBottomPadding={16}
      showCloseIcon={showCloseIcon}
      showBottomActions={showBottomActions}
      shouldDisableBottomPadding={showBottomActions}
      actionChildren={
        showBackgroundPicker ? (
          <Styled.SelectWrapper>
            <Styled.Select
              label="Background Mode"
              variant="standard"
              selectSize="small"
              value={selectedBackgroundMode}
            >
              {backgroundModes?.map((mode) => (
                <MenuItem
                  key={mode.value}
                  value={mode.value}
                  onClick={() => onBackgroundModeClick?.(mode.value)}
                >
                  {mode.label}
                </MenuItem>
              ))}
            </Styled.Select>
          </Styled.SelectWrapper>
        ) : null
      }
      modalId="add-image"
    >
      <Styled.SubTitle>{subtitle}</Styled.SubTitle>
      <Styled.DropZone
        {...getRootProps()}
        droppedImages={(props.files?.length ?? 0) > 0 && showPreviews}
        isLoading={isLoading || isDragAccept}
      >
        {props.files?.length && showPreviews ? (
          props.files.map((file) => (
            <FileItem
              key={file.lastModified}
              url={file.preview}
              filename={file.name}
              status={file.status}
            />
          ))
        ) : (
          <>
            <Styled.Input {...getInputProps()} />
            {isLoading ? (
              <>
                <Styled.Spinner
                  name="spinner"
                  wrapperClassName="spinnerIcon"
                  size={1}
                />
                <Styled.Paragraph pb="1em" color={mainColor} pt="0.5em">
                  {uploadingTextLabel}
                </Styled.Paragraph>
              </>
            ) : !isDragReject ? (
              <>
                <Styled.Paragraph pb="1em" color={mainColor}>
                  {dragTextLabel} {textPostfix}
                </Styled.Paragraph>
                {!isDragAccept && (
                  <Styled.UploadButton
                    variant="contained"
                    isDragActive={isDragActive}
                    size="large"
                    color={isDragActive ? 'primary' : 'secondary'}
                  >
                    {uploadButtonLabel}
                  </Styled.UploadButton>
                )}
              </>
            ) : (
              <Styled.Paragraph pb="1em" color={mainColor}>
                File is not an image
              </Styled.Paragraph>
            )}
          </>
        )}
      </Styled.DropZone>
      {!(isLoading || isDragAccept) && showLinkForm && (
        <>
          <Styled.Paragraph
            color={secondaryColor}
            pt="1em"
            pb="1em"
            lineHeight="22px"
            textAlign="center"
          >
            {orTextLabel}
          </Styled.Paragraph>
          <Styled.FieldWrapper>
            <Styled.TextField
              fullWidth
              isRounded
              autoComplete="off"
              value={linkValue}
              type="text"
              multiline={false}
              variant="filled"
              size="medium"
              placeholder={searchInputPlaceholder}
              onChange={handleLinkValueChange}
            />
            <Styled.SubmitButton
              variant="contained"
              size="medium"
              disabled={linkValue?.length === 0}
              color={linkValue?.length > 0 ? 'primary' : 'secondary'}
              onClick={handleSearchFormClick}
            >
              {searchInputButton}
            </Styled.SubmitButton>
          </Styled.FieldWrapper>
        </>
      )}
      {errorItem != null && (
        <>
          {errorItem?.errors?.map((e) => {
            let errorMsg = e.message;
            if (e.code === wrongFormatCode) {
              errorMsg = `${wrongFormatErrorLabel} ${DEFAULT_ALLOWED_FILES}`;
            } else if (e.code === tooManyFilesCode) {
              errorMsg = tooManyFilesErrorLabel;
            }
            return (
              <Styled.Paragraph
                pt="0.5em"
                pb="0"
                key={e.code}
                variant="micro"
                color={palette.error.main}
              >
                {errorMsg}
              </Styled.Paragraph>
            );
          })}
        </>
      )}
      {customErrors}
    </Styled.Dialog>
  );
};

export default UploadImageModal;
